import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './ThankYou.css';
import coinLogo from '../assets/COINLogo.png';

function ThankYou() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuthStatus } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const checkAuthStatusRef = useRef(checkAuthStatus);

  useEffect(() => {
    checkAuthStatusRef.current = checkAuthStatus;
  }, [checkAuthStatus]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const id = query.get('session_id');
    if (id) {
      setSessionId(id);
    } else {
      setVerificationStatus('failed');
      setError('Invalid page access. Please log in.');
    }
  }, [location.search]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    const maxAttempts = 12;
    const retryDelayMs = 2500;
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const verifyRegistration = async () => {
      setVerificationStatus('verifying');
      setStatusMessage('Verifying your free trial...');
      setError('');

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          const response = await fetch('/api/payment/verify-registration', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionId }),
          });

          const data = await response.json();
          if (cancelled) return;

          if (response.ok && data.success) {
            await checkAuthStatusRef.current();
            if (cancelled) return;
            setVerificationStatus('verified');
            setStatusMessage('');
            return;
          }

          if (response.status === 202 || data.status === 'pending' || data.retryable) {
            if (attempt < maxAttempts) {
              setStatusMessage(data.message || 'We are still verifying your free trial. This usually takes a few seconds.');
              await wait(retryDelayMs);
              continue;
            }

            setVerificationStatus('failed');
            setError('We are still waiting for Stripe to finish verification. Please refresh this page in a moment or contact support@cardboardprofit.com.');
            return;
          }

          setVerificationStatus('failed');
          setError(data.message || 'Card verification failed. Please register again with another valid card.');
          return;
        } catch (err) {
          console.error('Verification/Login error:', err);
          if (cancelled) return;

          if (attempt < maxAttempts) {
            setStatusMessage('Still checking your registration...');
            await wait(retryDelayMs);
            continue;
          }

          setVerificationStatus('failed');
          setError('An error occurred while verifying your registration. Please try again or contact support@cardboardprofit.com.');
          return;
        }
      }
    };

    verifyRegistration();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const handleGoToApp = () => {
    if (verificationStatus === 'verified') {
      navigate('/dashboard');
    }
  };

  const isVerifying = verificationStatus === 'verifying';
  const isVerified = verificationStatus === 'verified';
  const hasFailed = verificationStatus === 'failed';

  return (
    <div className="thank-you-container">
      <div className="thank-you-box">
        <div className="logo-container">
          <img src={coinLogo} alt="COIN Logo" />
        </div>

        <h2>{isVerified ? 'Thank You For Signing Up!' : 'Verifying Your Free Trial'}</h2>

        {isVerified && (
          <>
            <p>Before you dive into the data, watch this quick video - it will show you how to use COIN like a pro.</p>
            <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
              <iframe
                src="https://player.vimeo.com/video/1077620791?h=dc329c72d8&badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Getting Started with COIN"
                allowFullScreen
              ></iframe>
            </div>
          </>
        )}

        {isVerifying && <div className="loading-message">{statusMessage}</div>}
        {error && <div className="error-message">{error}</div>}

        {isVerified && !error && (
          <>
            <p className="thank-you-message">
              Your free 7-day trial is now active - you can start using COIN immediately. If it is not for you, you can cancel anytime during the trial.
            </p>
            <p><strong>You are one click away from seeing the top cards to grade in today's market.</strong></p>
            <button onClick={handleGoToApp} className="go-to-app-button">
              Go to App
            </button>
          </>
        )}

        {hasFailed && (
          <div className="auth-links">
            <a href="/register">Try Registering Again</a>
          </div>
        )}
      </div>
    </div>
  );
}

export default ThankYou;
